import React, { useCallback, useMemo, useState } from "react";
import { AccountingReportType } from "@common/types/reports/report-type";
import { ExpenseByVendorSort } from "@common/helpers/expenseByVendorSort";
import { ReportFormat } from "@common/helpers/reports";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { useExpenseByVendorQuery } from "@api/accountingReports.api";
import { DownloadDropdown } from "@components/accounting/balanceSheet/DownloadDropdown";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { expenseByVendorFilterDefinition } from "../accountingReportFilterDefinitions";
import { AccountingReportViewToolbar } from "../reportViews/AccountingReportViewToolbar";
import { useAccountingTabContext } from "../useAccountingTab";
import { useGlDrilldownDrawerState } from "../useGlDrilldownDrawerState";
import { ExpenseByVendorContent } from "./ExpenseByVendorContent";
import { useExpenseByVendorDownload } from "./useExpenseByVendorDownload";

export const ExpenseByVendorPage: React.FC = () => {
    const { filters, setFilters, selectedEntity, ledgerBasis } =
        useAccountingTabContext();
    const {
        openDrillDown,
        closeDrillDown,
        isOpen: isGlDrawerOpen,
        drawerKey,
        drawerTarget,
    } = useGlDrilldownDrawerState();
    const filtersWithDefaultValues = useMemo(() => {
        if (filters.cycle === undefined) {
            return {
                ...filters,
                cycle: expenseByVendorFilterDefinition.defaultCycle,
            };
        }
        return filters;
    }, [filters]);

    const [sort, setSort] = useState<ExpenseByVendorSort>(
        ExpenseByVendorSort.VENDOR_ASC,
    );

    const handleFiltersChange = useCallback<typeof setFilters>(
        (nextFilters) => {
            setSort(ExpenseByVendorSort.VENDOR_ASC);
            setFilters(nextFilters);
        },
        [setFilters],
    );

    const { expenseByVendor, generationDate } = useExpenseByVendorQuery(
        filtersWithDefaultValues,
    );

    const { isDownloading, download } = useExpenseByVendorDownload(
        filtersWithDefaultValues,
        sort,
        expenseByVendor.data?.presentation?.rowVisibility,
    );

    const handleDownload = useCallback(
        (format: ReportFormat) => {
            if (!selectedEntity) {
                return;
            }
            download({
                entity: selectedEntity,
                format,
                date: filtersWithDefaultValues.endDate,
            });
        },
        [download, selectedEntity, filtersWithDefaultValues.endDate],
    );

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_EXPENSE_BY_VENDOR_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    return (
        <>
            <AccountingReportViewToolbar
                reportId="expenseByVendor"
                filters={filtersWithDefaultValues}
                setFilters={handleFiltersChange}
            >
                <DownloadDropdown
                    onDownload={handleDownload}
                    loading={isDownloading}
                    disabled={
                        expenseByVendor.isLoading || !expenseByVendor.isSuccess
                    }
                />
            </AccountingReportViewToolbar>
            <AccountingReportFiltersForm
                {...expenseByVendorFilterDefinition.filterProps}
                showFinancialAccountFilter
                filters={filtersWithDefaultValues}
                setFilters={handleFiltersChange}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                shouldTrackDatePreset
                hideSearch
                requireDateSelection
                showCyclesSelect
                reportType={AccountingReportType.EXPENSE_BY_VENDOR}
            />
            {selectedEntity && (
                <LoaderOnInitialLoading
                    queryResult={expenseByVendor}
                    dimWhileFetching
                >
                    {expenseByVendor.data && selectedEntity && (
                        <ExpenseByVendorContent
                            data={expenseByVendor.data}
                            filters={filtersWithDefaultValues}
                            chrome={expenseByVendor.data.presentation.chrome}
                            numberFormat={
                                expenseByVendor.data.presentation.numberFormat
                            }
                            generationDate={generationDate}
                            sort={sort}
                            onSortChange={setSort}
                            onDrillDown={openDrillDown}
                        />
                    )}
                </LoaderOnInitialLoading>
            )}
            {drawerTarget && (
                <GlDrilldownDrawer
                    key={drawerKey}
                    open={isGlDrawerOpen}
                    onClose={closeDrillDown}
                    filters={drawerTarget.filters}
                    entityIds={drawerTarget.entityIds}
                    ledgerBasis={ledgerBasis}
                    classifications={drawerTarget.classifications}
                    pnlBalanceSince={drawerTarget.pnlBalanceSince}
                />
            )}
        </>
    );
};
